/**
 * Pure state derivation for the live training-run monitor (frame 7b), fed by
 * `Model.trainingProgress` — the snapshot the trainer writes at every eval step
 * (W7-TR-01). Every figure here is measured by the trainer; no planning
 * estimate and no interpolated point is mixed in.
 *
 * The honesty precedence lives HERE, not in template branch order:
 *
 *   - the version is not running        → `idle` (the monitor does not render)
 *   - running but no snapshot yet       → `starting`, which structurally
 *                                         carries NO numbers
 *   - a snapshot exists                 → `running` with the measured values
 *
 * The component mechanically switches on `kind`, so reordering its branches
 * cannot make a "starting" run show a fabricated step or loss. Behaviourally
 * pinned in tests/trainingProgress.test.ts (node-env vitest, hence the explicit
 * import — no auto-import reliance).
 */
import type { components } from "@scalty/contracts";
import { CUSTOMER_TERMS } from "./terms";

type TrainingProgress = components["schemas"]["TrainingProgress"];
type ModelStatus = components["schemas"]["ModelStatus"];
type TrainingState = components["schemas"]["TrainingState"];
type TrainingWait = components["schemas"]["TrainingWait"];

/** Matches LossCurveChart's point shape so the live series feed the same chart. */
export interface MonitorLossPoint {
  step: number;
  loss: number;
}

export type TrainingMonitorState =
  | { kind: "idle" }
  | { kind: "starting"; headline: string; detail: string }
  | { kind: "waiting"; headline: string; detail: string }
  | { kind: "failed"; headline: string; detail: string }
  | {
      kind: "running";
      step: number;
      maxSteps: number;
      /** 0–100, clamped; 0 when the trainer has not resolved a step budget. */
      percent: number;
      stepLabel: string;
      epochLabel: string;
      trainSeries: MonitorLossPoint[];
      evalSeries: MonitorLossPoint[];
      /** Latest measured train loss, or "—" before the first training log. */
      trainLossLabel: string;
      /** Latest measured eval loss, or "—" before the first evaluation. */
      evalLossLabel: string;
      /** null when the trainer does not report throughput — the tile is omitted, not zeroed. */
      throughputLabel: string | null;
      updatedLabel: string;
    };

export interface TrainingMonitorInput {
  /** The version's live status; the monitor only exists for a run in flight. */
  status: ModelStatus | null | undefined;
  /** The trainer's snapshot; null until the first progress artifact is written. */
  progress: TrainingProgress | null | undefined;
  /** Launcher-neutral execution state; distinct from optimizer progress. */
  trainingState?: TrainingState | null;
  /** Observed wait duration/reason, present only while waiting for capacity. */
  trainingWait?: TrainingWait | null;
  /** Terminal failure reason from the API, rendered verbatim. */
  error?: string | null;
  /** Wall-clock now, in ms — passed in so the derivation stays pure. */
  now: number;
}

/** A run is "in flight" while it is admitted to the queue or actually training. */
export function isRunInFlight(status: ModelStatus | null | undefined): boolean {
  return status === "queued" || status === "training";
}

/** "updated 12 s ago" — the freshness of the snapshot, never a fabricated heartbeat. */
export function formatUpdatedAgo(updatedAt: string, now: number): string {
  const at = Date.parse(updatedAt);
  if (Number.isNaN(at)) return "update time unavailable";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `updated ${seconds} s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `updated ${minutes} min ago`;
  return `updated ${Math.floor(minutes / 60)} h ago`;
}

const CHARGING_COPY = "Nothing is charged while you wait.";
const NEUTRAL_WAIT_DETAIL = `A machine is being started for this run. ${CHARGING_COPY}`;

// An unreadable status says so (W7-DS-02); every other neutral wait keeps the
// single frame sentence (W7-TR-03 T3). Both end with the same charging helper.
const WAIT_REASON_COPY: Partial<Record<TrainingWait["reason"], string>> = {
  preempted: `The machine was taken back by the provider; getting another. ${CHARGING_COPY}`,
  status_unavailable: `Status is temporarily unavailable; the run keeps waiting. ${CHARGING_COPY}`,
  capacity_rising: `A machine is being built for this run. ${CHARGING_COPY}`,
  image_fetching: `The machine is ready; the software is being copied onto it. ${CHARGING_COPY}`,
  loading: `The software is running and the model is being read into memory. ${CHARGING_COPY}`,
};

export function trainingWaitCopy(wait: TrainingWait): { headline: string; detail: string } {
  const minutes = Math.max(0, Math.floor(wait.waitedSeconds / 60));
  return {
    headline: `${CUSTOMER_TERMS.states.waitingForGpu} · ${minutes} min`,
    detail: WAIT_REASON_COPY[wait.reason] ?? NEUTRAL_WAIT_DETAIL,
  };
}

/**
 * A measured loss, to four decimals — the precision a person can read and compare. The
 * finished-run projection printed the stored double as it came (`0.123456789012345678`),
 * so the same run's number was four digits long while it ran and eighteen after it ended
 * (W8-UI-07).
 */
export function formatLoss(value: number | null | undefined): string {
  return typeof value === "number" ? value.toFixed(4) : "—";
}

function formatEpoch(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function toSeries(points: { step: number; value: number }[]): MonitorLossPoint[] {
  return points.map((p) => ({ step: p.step, loss: p.value }));
}

export function trainingMonitorState(input: TrainingMonitorInput): TrainingMonitorState {
  if (input.status === "failed" && input.error) {
    return { kind: "failed", headline: CUSTOMER_TERMS.states.didNotFinish, detail: input.error };
  }
  if (!isRunInFlight(input.status)) return { kind: "idle" };

  if (input.trainingState === "waiting_for_capacity" && input.trainingWait) {
    return { kind: "waiting", ...trainingWaitCopy(input.trainingWait) };
  }

  const progress = input.progress ?? null;
  if (progress === null) {
    return input.status === "queued"
      ? {
          kind: "starting",
          headline: "Getting a machine ready",
          detail: "The run is waiting. Progress appears once training starts.",
        }
      : {
          kind: "starting",
          headline: "Starting",
          detail: "The model is being prepared. Progress appears at the first check.",
        };
  }

  const maxSteps = progress.maxSteps;
  const percent =
    maxSteps > 0 ? Math.min(100, Math.max(0, (progress.step / maxSteps) * 100)) : 0;
  const evalSeries = toSeries(progress.evalLoss);

  return {
    kind: "running",
    step: progress.step,
    maxSteps,
    percent,
    stepLabel: maxSteps > 0 ? `step ${progress.step} / ${maxSteps}` : `step ${progress.step}`,
    epochLabel: formatEpoch(progress.epoch),
    trainSeries: toSeries(progress.trainLoss.series),
    evalSeries,
    trainLossLabel: formatLoss(progress.trainLoss.latest),
    evalLossLabel: formatLoss(evalSeries.length > 0 ? evalSeries[evalSeries.length - 1]!.loss : null),
    throughputLabel:
      progress.tokensPerSecond === null
        ? null
        : `${Math.round(progress.tokensPerSecond).toLocaleString()} tok/s`,
    updatedLabel: formatUpdatedAgo(progress.updatedAt, input.now),
  };
}

/**
 * The state word a model version wears on every list, pane and page.
 *
 * Preparation is the FIRST STAGE OF TRAINING, not a state of the dataset
 * (founder, 2026-09-21): the contract already says so — `trainingState`
 * `preparing_dataset` means the version the run will read is still being built,
 * and nothing is charged for it. The portal used to render that as "Queued",
 * which told the customer nothing about what was going on.
 */
export function trainingStatusWord(input: {
  status: ModelStatus | null | undefined;
  trainingState?: TrainingState | null;
}): string {
  if (input.trainingState === "preparing_dataset") return CUSTOMER_TERMS.states.preparing;
  if (input.trainingState === "waiting_for_capacity") return CUSTOMER_TERMS.states.waiting;
  switch (input.status) {
    case "queued":
      return "Queued";
    case "training":
      return "Training";
    case "succeeded":
      return CUSTOMER_TERMS.states.ready;
    case "failed":
      return "Failed";
    case "cancelled":
      return CUSTOMER_TERMS.states.stopped;
    default:
      return CUSTOMER_TERMS.states.unavailable;
  }
}

/**
 * What is happening right now, in one line the customer's own words: which
 * stage the run is at and why it is where it is. Names no accelerator, no node,
 * no job and no base model — the stage, and the measured figure that goes with
 * it (founder, 2026-09-21, note 25).
 *
 * Every branch reads a fact the contract already carries. Where there is no
 * fact the line says what is being waited for and nothing more; it never
 * invents a duration, a position or a percentage.
 */
export function trainingStageLine(input: {
  status: ModelStatus | null | undefined;
  trainingState?: TrainingState | null;
  trainingWait?: TrainingWait | null;
  progress?: TrainingProgress | null;
}): string {
  if (input.trainingState === "preparing_dataset") return "Preparing the training data";
  if (input.trainingState === "waiting_for_capacity") {
    // The observed wait, said so it reads at any length: "0 min" told the customer nothing in
    // the first minute. `trainingWaitCopy` keeps its own whole-minute headline for the monitor.
    return input.trainingWait
      ? `${CUSTOMER_TERMS.states.waitingForGpu} · ${formatLiveDuration(input.trainingWait.waitedSeconds)}`
      : CUSTOMER_TERMS.states.waitingForGpu;
  }
  switch (input.status) {
    case "queued":
      return "Waiting to start";
    case "training":
      return input.progress && input.progress.maxSteps > 0
        ? `Training · step ${input.progress.step} of ${input.progress.maxSteps}`
        : "Training has started";
    case "succeeded":
      return "Finished";
    case "failed":
      // The stage is the stage; why it ended is a sentence of its own, shown once beside the
      // state rather than stuffed into a four-word tile (founder, 2026-09-21).
      return CUSTOMER_TERMS.states.didNotFinish;
    case "cancelled":
      return CUSTOMER_TERMS.states.stopped;
    default:
      return CUSTOMER_TERMS.states.unavailable;
  }
}

/**
 * Wall-clock seconds a run has been going, measured from the moment the stage
 * that is running actually began. Null when nothing is in flight or when no
 * start moment has been recorded: the screen then says nothing rather than
 * counting from an invented zero.
 */
export function runningSeconds(input: {
  status: ModelStatus | null | undefined;
  startedAt?: string | null;
  waitingSince?: string | null;
  createdAt?: string | null;
  now: number;
}): number | null {
  if (!isRunInFlight(input.status)) return null;
  const from = input.startedAt ?? input.waitingSince ?? input.createdAt ?? null;
  if (from === null) return null;
  const at = Date.parse(from);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((input.now - at) / 1000));
}

/**
 * A duration that is still growing, written so it visibly grows: seconds inside the first
 * minute, minutes and seconds inside the first hour, hours and minutes beyond it. Distinct from
 * `formatRunDuration`, which states a settled duration and rounds to whole minutes — a figure
 * that only changes once a minute cannot be the sign that work is happening (founder, note 25).
 */
export function formatLiveDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ${seconds % 60} s`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}
